import { computed, DestroyRef, effect, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BaseViewModel } from '../base-view-model';
import { ProgressModel } from '../progress/progress.model';
import { FavoriteProduct } from '@common/favorite-grid/model';
import { Toast } from '@helper/toast';
import { MetaData } from '@helper/meta-data';
import { Constants } from '@helper/constants';
import { RoutePaths } from '@helper/route-paths';
import {
  AddFavoriteRequest,
  DeleteFavoriteRequest,
  Favorite,
  MergeFavoritesRequest,
} from '@proto/store/api/store/misc/favorite/favorite';
import { AuthStateService } from '@service/auth-state.service';
import { SafeStorageService } from '@service/safe-storage.service';
import { FavoriteRepositoryService } from '@service/favorite-repository.service';
import { SidebarTab } from '../../misc/sidebar-tab';
import { Product } from '@proto/store/api/store/product';
import { EventTracker } from '../../analytics/event-tracker.service';

import { isEqual } from 'lodash-es';
import { finalize } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FavoriteModel extends BaseViewModel<Favorite[] | undefined> {
  readonly favorites = signal<string[]>([]);
  readonly products = computed(() => this.allProducts()?.slice(0, this.itemCount()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));

  private readonly allProducts = computed(() => FavoriteProduct.favoriteToProducts(this.apiResult()), {
    equal: (a, b) => isEqual(a, b),
  });

  constructor(
    private lativRepository: FavoriteRepositoryService,
    private authService: AuthStateService,
    private eventTracker: EventTracker,
    private progressModel: ProgressModel,
    private localService: SafeStorageService
  ) {
    super(undefined, true);
    this.favorites.set(this.localService.getFavorites() ?? []);
    effect(() => localService.setFavorites(this.favorites()));
  }

  syncFavorites(ref: DestroyRef, callback?: () => void): void {
    // 會員頁面與收藏頁面 ngOnInit 使用. 同步收藏商品.
    // local 會一直 effect, 所以 sync 可以直接從 local 塞就好.
    this.favorites.set(this.localService.getFavorites() ?? []);
    this.setApiResult(undefined);
    this.lativRepository
      .listFavorites()
      .pipe(
        takeUntilDestroyed(ref),
        finalize(() => {
          if (callback) {
            callback();
          }
        })
      )
      .subscribe({
        next: res => {
          this.setApiResult(res.favorites);
          // 只有 API 有的, 塞到 local.
          const productIds = res.favorites.filter(i => i.item?.meta?.productId).map(i => i.item?.meta?.productId ?? '');
          const onlineFavorites = productIds.filter(item => !this.favorites().includes(item));
          this.favorites.update(items => [...items, ...onlineFavorites]);
          // 只有 local 有的, 新增回去資料庫.
          const localFavorites = this.favorites().filter(item => !productIds.includes(item));
          if (localFavorites.length) {
            this.mergeFavorites(ref, localFavorites);
          }
        },
        error: err => this.setApiError(err),
      });
  }

  toggleFavorite(ref: DestroyRef, productId: string, toast: MatSnackBar, product?: Product): void {
    // 商品頁使用.
    if (this.favorites().some(i => i === productId)) {
      this.deleteFavorite(ref, productId, toast);
    } else {
      if (product) {
        this.eventTracker.onFavorite(product);
      }
      this.addFavorite(ref, productId, toast);
    }
  }

  deleteFavorite(ref: DestroyRef, productId: string, toast: MatSnackBar): void {
    // 收藏頁與商品頁使用. 商品頁可離線刪除.
    this.favorites.update(items => items.filter(i => i != productId));
    if (this.authService.isLogin()) {
      this.lativRepository
        .deleteFavorite(
          DeleteFavoriteRequest.fromPartial({
            productId,
          })
        )
        .pipe(takeUntilDestroyed(ref))
        .subscribe({
          next: () => {
            this.setApiResult(this.apiResult()?.filter(i => i.item?.meta?.productId != productId));
            this.favorites.update(items => items.filter(i => i != productId));
            Toast.open(toast, $localize`取消收藏`);
          },
          error: err => this.setApiError(err),
        });
    }
  }

  clearFavorites(ref: DestroyRef): void {
    // 收藏頁使用.
    this.progressModel.show();
    this.lativRepository
      .clearFavorites()
      .pipe(
        takeUntilDestroyed(ref),
        finalize(() => this.progressModel.hide())
      )
      .subscribe({
        next: () => {
          this.setApiResult([]);
          this.favorites.set([]);
        },
        error: err => this.setApiError(err),
      });
  }

  override getMetaByUrl(segments: string[]): MetaData | undefined {
    if (segments[1] !== RoutePaths.FAVORITE) {
      return undefined;
    }
    return new MetaData(SidebarTab.favTabTitle);
  }

  private addFavorite(ref: DestroyRef, productId: string, toast: MatSnackBar): void {
    // 商品頁可離線新增.
    Toast.open(toast, $localize`收藏成功`);
    this.favorites.update(items => [...items, productId]);
    if (this.authService.isLogin()) {
      this.lativRepository
        .addFavorite(
          AddFavoriteRequest.fromPartial({
            productId,
          })
        )
        .pipe(takeUntilDestroyed(ref))
        .subscribe({
          error: () => {
            // ignore error
          },
        });
    }
  }

  private mergeFavorites(ref: DestroyRef, productIds: string[]): void {
    if (!this.authService.isLogin()) {
      return;
    }
    this.lativRepository
      .mergeFavorite(
        MergeFavoritesRequest.fromPartial({
          productIds,
        })
      )
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        error: () => {
          // ignore error
        },
      });
  }
}
